import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, Badge, Checkbox } from '@fyos/components';
import { GripVertical, MessageSquare, Skull, Clock, ChevronUp, ChevronDown } from 'lucide-react';
import type { Item, Category } from '@/lib/itemApi';

function extractPreviewText(description: string): string {
  if (!description) return '';

  const tmp = document.createElement('div');
  tmp.innerHTML = description;

  // Insert newlines before block-level elements so textContent doesn't merge them
  const blocks = tmp.querySelectorAll('h1, h2, h3, h4, h5, h6, p, div, li, br, tr, blockquote, pre');
  blocks.forEach(el => {
    el.insertBefore(document.createTextNode('\n'), el.firstChild);
  });

  // Collect heading texts before flattening
  const headingTexts = new Set<string>();
  tmp.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(el => {
    const t = (el.textContent || '').trim();
    if (t) headingTexts.add(t);
  });

  // Also detect markdown headings from the raw source
  const mdHeadingPattern = /^#{1,6}\s+(.+)$/gm;
  let match: RegExpExecArray | null;
  while ((match = mdHeadingPattern.exec(description)) !== null) {
    headingTexts.add(match[1].trim());
  }

  const plain = tmp.textContent || tmp.innerText || '';
  const lines = plain.split(/\n/).map(l => l.trim()).filter(Boolean);
  if (lines.length === 0) return '';

  // Find the first line that is NOT a heading
  const firstParagraph = lines.find(line => !headingTexts.has(line));
  if (firstParagraph) return firstParagraph;

  // Only headings exist — return them newline-separated
  return lines.filter(line => headingTexts.has(line)).join('\n');
}

export interface BoardItemCardProps {
  item: Item;
  category?: Category;
  onClick: () => void;
  isDragDisabled?: boolean;
  isSelected?: boolean;
  onSelect?: (itemId: string, selected: boolean) => void;
  onVote?: (itemId: string, direction: 'up' | 'down') => void;
}

function getDaysSince(dateStr: string): number {
  const created = new Date(dateStr).getTime();
  const now = Date.now();
  return Math.floor((now - created) / 86400000);
}

function formatDaysAgo(days: number): string {
  if (days === 0) return 'today';
  if (days === 1) return '1d';
  return `${days}d`;
}

export default function BoardItemCard({
  item,
  category,
  onClick,
  isDragDisabled = false,
  isSelected = false,
  onSelect,
  onVote,
}: BoardItemCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    data: { item, type: 'item' },
    disabled: isDragDisabled,
  });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
  };

  const daysSince = getDaysSince(item.created_at);
  const killReason = item.kill_reason || item.metadata?.kill_reason;

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <Card
        variant="elevated"
        padding="sm"
        className={`group hover:shadow-theme-md transition-shadow rounded-md ${isSelected ? 'ring-2 ring-brand-500' : ''}`}
        onClick={onClick}
      >
        <div className="flex items-start gap-1.5">
          {/* Stacked checkbox + grab handle in a single column */}
          {(onSelect || !isDragDisabled) && (
            <div className={`flex flex-col items-center gap-0.5 flex-shrink-0 transition-opacity ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
              {onSelect && (
                <div onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={isSelected}
                    onChange={(e) => {
                      e.stopPropagation();
                      onSelect(item.id, e.target.checked);
                    }}
                  />
                </div>
              )}
              {!isDragDisabled && (
                <button
                  className="cursor-grab text-gray-300 dark:text-gray-500 active:cursor-grabbing"
                  {...listeners}
                  onClick={(e) => e.stopPropagation()}
                  aria-label="Drag to move item"
                >
                  <GripVertical className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
          <div className="min-w-0 flex-1">
            {/* Title */}
            <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {item.title}
            </h3>

            {/* Description — 3 lines max, plain text only */}
            {item.description && (() => {
              const preview = extractPreviewText(item.description);
              if (!preview) return null;
              return (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 line-clamp-3 whitespace-pre-line">
                  {preview}
                </p>
              );
            })()}

            {/* Tags + age badge + category */}
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {item.tags && item.tags.slice(0, 2).map(tag => (
                <Badge
                  key={tag.id}
                  size="sm"
                  style={{ backgroundColor: tag.color + '20', color: tag.color }}
                >
                  {tag.name}
                </Badge>
              ))}
              {item.tags && item.tags.length > 2 && (
                <Badge size="sm" variant="gray">
                  +{item.tags.length - 2}
                </Badge>
              )}
              {item.board_column !== 'shipped' && item.board_column !== 'killed' && (
                <Badge size="sm" variant="gray">
                  <Clock className="h-2.5 w-2.5 mr-0.5" />
                  {formatDaysAgo(daysSince)}
                </Badge>
              )}
              {category && (
                <Badge
                  size="sm"
                  style={{ backgroundColor: category.color + '20', color: category.color }}
                >
                  {category.name}
                </Badge>
              )}
            </div>

            {/* Kill reason (only for killed items) */}
            {item.board_column === 'killed' && killReason && (
              <div className="mt-2 flex items-start gap-1.5 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800/50 px-2 py-1.5">
                <Skull className="h-3 w-3 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-[11px] text-red-600 dark:text-red-400 line-clamp-2">{killReason}</p>
              </div>
            )}

            {/* Divider */}
            <div className="mt-2 border-t border-gray-100 dark:border-gray-700" />

            {/* Footer: votes + comments */}
            <div className="mt-1.5 flex items-center justify-between text-gray-400 dark:text-gray-500">
              <div className="flex items-center gap-0">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onVote?.(item.id, 'up'); }}
                  className={`rounded p-0.5 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    item.user_vote === 1 ? 'text-brand-500' : ''
                  }`}
                  title={item.user_vote === 1 ? 'Remove upvote' : 'Upvote'}
                >
                  <ChevronUp className="h-3.5 w-3.5" />
                </button>
                <span className={`text-[11px] font-medium min-w-[1rem] text-center ${
                  item.user_vote === 1 ? 'text-brand-500' : item.user_vote === -1 ? 'text-red-500' : ''
                }`}>{item.vote_count || 0}</span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onVote?.(item.id, 'down'); }}
                  className={`rounded p-0.5 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    item.user_vote === -1 ? 'text-red-500' : ''
                  }`}
                  title={item.user_vote === -1 ? 'Remove downvote' : 'Downvote'}
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="flex items-center gap-1">
                <MessageSquare className="h-3 w-3" />
                <span className="text-[11px] font-medium">{item.comment_count || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
